import React from 'react';
import { Calendar, MessageSquare, ClipboardList, Rocket, ArrowRight, CheckCircle } from 'lucide-react';
import { trackCTAClick } from '../utils/analytics';

const steps = [
  {
    icon: Calendar,
    number: '01',
    title: 'You Book Your Session',
    description: 'Choose a time that works for you. Your complimentary strategy session is a focused 30-minute conversation.',
    details: ['No commitment required', 'Virtual or in-person options', 'Flexible scheduling'],
    color: 'cyan',
  },
  {
    icon: MessageSquare,
    number: '02',
    title: 'We Listen & Assess',
    description: "We take time to understand your organization's unique challenges, goals, and culture before recommending anything.",
    details: ['Deep-dive into your pain points', 'Assess team dynamics', 'Identify quick wins'],
    color: 'violet',
  },
  {
    icon: ClipboardList,
    number: '03',
    title: 'You Get a Custom Plan',
    description: 'Within 48 hours, you receive a tailored strategy with clear recommendations, timelines, and expected outcomes.',
    details: ['Actionable roadmap', 'Budget-conscious options', 'Funding guidance if eligible'],
    color: 'emerald',
  },
  {
    icon: Rocket,
    number: '04',
    title: 'Transformation Begins',
    description: "We launch your program with hands-on workshops, coaching sessions, and measurable milestones from day one.",
    details: ['Immediate engagement', 'Ongoing support', 'Measurable results within 30 days'],
    color: 'amber',
  },
];

const colorMap = {
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', line: 'from-cyan-500' },
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/20', text: 'text-violet-400', line: 'from-violet-500' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', line: 'from-emerald-500' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', line: 'from-amber-500' },
};

const AfterYouBook = () => {
  const scrollToConsultation = () => {
    trackCTAClick('Book your session now', 'after_you_book');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="after-you-book" className="py-16 sm:py-24 bg-slate-950" data-testid="after-you-book-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-cyan-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Your Path Forward
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            What Happens
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
              After You Book
            </span>
          </h2>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto">
            No guesswork. No surprises. Here's exactly what to expect when you take the next step.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, i) => {
            const c = colorMap[step.color];
            return (
              <div
                key={step.number}
                className="relative p-5 sm:p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all duration-300 group"
                data-testid={`after-book-step-${i}`}
              >
                {/* Step Number */}
                <span className="absolute top-4 right-4 text-4xl font-black text-white/[0.03] group-hover:text-white/[0.06] transition-colors">
                  {step.number}
                </span>

                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl ${c.bg} ${c.border} border flex items-center justify-center mb-4`}>
                  <step.icon className={`h-6 w-6 ${c.text}`} />
                </div>

                <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.description}</p>

                <ul className="space-y-1.5">
                  {step.details.map((d) => (
                    <li key={d} className="flex items-center gap-2 text-slate-300 text-xs">
                      <CheckCircle className={`h-3 w-3 ${c.text} flex-shrink-0`} />
                      {d}
                    </li>
                  ))}
                </ul>

                {/* Connector arrow (hidden on last card and mobile) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                    <ArrowRight className="h-5 w-5 text-white/10" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-10 sm:mt-12">
          <button
            onClick={scrollToConsultation}
            data-testid="after-book-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 hover:border-white/20 transition-all text-sm"
          >
            <Calendar className="h-4 w-4 text-cyan-400" />
            Book your session now
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default AfterYouBook;
